import instance, { bigNumberTransformResponse } from '@/jmHttp/index';
import { search } from '@/jmHttp/interface';
import { getGlobalSearchCostTimeTrackConfig } from '@/utils/globalSearchTrack';
import slsLogger from '@/utils/slsLogger';

// 全局搜索翻页续传参数字段（链式游标：后端每页返回 res.sortColumnCodes.backExtra，下一页请求须携带最新值）
const GLOBAL_SEARCH_CONTINUATION_REQUEST_FIELD = 'backExtra';
const GLOBAL_SEARCH_CAN_FUZZY_QUERY_FIELD = 'canFuzzyQuery';
const GLOBAL_SEARCH_PAGE_FIELDS = ['page', 'pageNum', 'pageNo', 'current', 'currentPage'];
const GLOBAL_SEARCH_SIZE_FIELDS = ['size', 'pageSize'];
const globalSearchContinuationCache = new Map();

const isPlainObject = (value) => Object.prototype.toString.call(value) === '[object Object]';
const isEmptySessionValue = (value) => {
  if (typeof value === 'undefined' || value === null || value === '') return true;
  if (Array.isArray(value)) return value.length === 0;
  if (isPlainObject(value)) return Object.keys(value).length === 0;
  return false;
};

const stableSortValue = (value) => {
  if (Array.isArray(value)) return value.map(stableSortValue).filter((item) => !isEmptySessionValue(item));
  if (!isPlainObject(value)) return value;
  return Object.keys(value)
    .sort()
    .reduce((result, key) => {
      const normalizedValue = stableSortValue(value[key]);
      if (!isEmptySessionValue(normalizedValue)) {
        result[key] = normalizedValue;
      }
      return result;
    }, {});
};

const buildGlobalSearchSessionKey = (payload) => {
  if (!payload?.type && payload?.type !== 0) return '';
  const normalizedPayload = { ...payload };
  [...GLOBAL_SEARCH_PAGE_FIELDS, ...GLOBAL_SEARCH_SIZE_FIELDS].forEach((field) => {
    delete normalizedPayload[field];
  });
  delete normalizedPayload[GLOBAL_SEARCH_CONTINUATION_REQUEST_FIELD];
  delete normalizedPayload[GLOBAL_SEARCH_CAN_FUZZY_QUERY_FIELD];
  return `${normalizedPayload.type}::${JSON.stringify(stableSortValue(normalizedPayload))}`;
};

/**
 * 构建全局搜索 slsLogger 上报的 extra 字段。
 * 用 params 包裹请求参数，避免与 code/total 等响应字段平铺混淆；剔除 backExtra 续传游标（体积大且无分析价值）。
 * @param {Object} requestData - 实际发送的请求参数
 * @returns {{ params: Object }} 上报用的 extra 对象
 */
const buildGlobalSearchLogExtra = (requestData) => {
  const params = { ...requestData };
  delete params[GLOBAL_SEARCH_CONTINUATION_REQUEST_FIELD];
  return { params };
};

// 全局搜索接口
export const globalSearchRequest = async (data, options = {}) => {
  const { needTrack = false } = options;
  const requestData = { [GLOBAL_SEARCH_CAN_FUZZY_QUERY_FIELD]: 1, ...data };
  const page = Number(requestData?.page ?? 0);
  const hasType = requestData?.type || requestData?.type === 0;
  const sessionKey = hasType ? buildGlobalSearchSessionKey(requestData) : '';

  if (sessionKey) {
    if (page <= 1) {
      globalSearchContinuationCache.delete(sessionKey);
    } else if (globalSearchContinuationCache.has(sessionKey)) {
      requestData[GLOBAL_SEARCH_CONTINUATION_REQUEST_FIELD] = globalSearchContinuationCache.get(sessionKey);
    }
  }

  const config = getGlobalSearchCostTimeTrackConfig({
    needTrack,
    queryParams: { type: requestData.type },
    trackData: requestData
  });
  slsLogger.console({
    component: 'GlobalSearchApi',
    level: 'info',
    message: '全局搜索-接口请求',
    extra: buildGlobalSearchLogExtra(requestData)
  });
  return instance
    .post(search.globalSearch, requestData, {
      ...config,
      transformResponse: bigNumberTransformResponse
    })
    .then((res) => {
      // 链式游标：每页响应都可能返回下一页的新 token，需逐页更新缓存
      if (sessionKey) {
        const continuationValue = res?.sortColumnCodes?.[GLOBAL_SEARCH_CONTINUATION_REQUEST_FIELD] ?? null;
        // 后端返回有效 token 时覆盖缓存；返回空（通常是最后一页）时清除，避免后续请求携带失效游标
        if (continuationValue !== null && continuationValue !== '' && continuationValue !== 'null') {
          globalSearchContinuationCache.set(sessionKey, continuationValue);
        } else {
          globalSearchContinuationCache.delete(sessionKey);
        }
      }
      slsLogger.console({
        component: 'GlobalSearchApi',
        level: 'info',
        message: '全局搜索-接口请求成功',
        // 不上报完整 res：搜索结果体积大且高频，易导致 SLS 批量发送超限与主线程序列化开销
        extra: {
          ...buildGlobalSearchLogExtra(requestData),
          code: res?.code,
          total: res?.total,
          rowsLength: res?.rows?.length
        }
      });
      return res;
    })
    .catch((err) => {
      slsLogger.console({
        component: 'GlobalSearchApi',
        level: 'error',
        message: '全局搜索-接口请求失败',
        extra: { ...buildGlobalSearchLogExtra(requestData), error: err?.message }
      });
      throw err;
    });
};

// 搜索结果今日新增数量（type=2/11/12/13 走 search-by-type 中间层，type=14 走公众号文章列表逻辑）
export const todayDocCount = async (data) => {
  return instance.post(search.todayDocCount, data);
};
